import { useCallback, useEffect, useMemo, useState } from "react";
import { useFilterContext } from "../../../hooks/FilterContext";
import { useLazyFetchHubwiseJobsQuery } from "../../../services/headofficeadmin";

const limit = 50;

const useHubwiseJobs = () => {
    const { filter } = useFilterContext();

    const [cacheKey, setCacheKey] = useState(new Date().valueOf());
    const [fetchTrigger, { data, isError, error, isFetching }] = useLazyFetchHubwiseJobsQuery();
    const { pagination, results } = (!isError && data) || { pagination: { page: 1, pageSize: limit, totalCount: 0, totalPages: 1 }, results: [] };
    const { totalCount, page, totalPages } = pagination;

    const [sort, setSort] = useState(null);
    const [sortProperty, direction] = useMemo(() => sort?.split("+") ?? [], [sort]);

    const [isClearing, setIsClearing] = useState(false);

    const clear = async () => {
        if (isFetching)
            return;

        setIsClearing(true)

        const newCacheKey = new Date().valueOf();
        try {
            await fetchTrigger({ page: 1, limit, sort, cacheKey: newCacheKey, filter })
                .unwrap()
            setCacheKey(newCacheKey);
        } finally {
            setIsClearing(false);
        }
    };

    const isItemLoaded = (index) => !(totalCount > 0 && page < totalPages) || index < results.length;

    const loadMore = () => {
        if (isFetching) return;

        fetchTrigger({ cacheKey, page: page + 1, limit, sort, filter });
    };

    const onClickSort = useCallback((property) => {
        let newDirection = "";

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
    }, [direction, sortProperty]);

    const reload = (e) => {
        if (e && typeof (e.preventDefault) === 'function') {
            e.preventDefault();
        }

        clear();
    };

    useEffect(() => {
        // Don't fetch more if we're already fetching
        if (isFetching)
            return;

        clear();
        // Disabled eslint warning because we only want to run this effect when the filter or sort changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, sort]);

    // Convenience variable to determine whether to show loading rows or data
    const hasData = useMemo(
        () => !(isClearing || isError || results.some(x => x == null)),
        [isClearing, isError, results]
    );

    return {
        results,
        sort,
        onClickSort,
        totalCount,
        hasData,
        isClearing,
        isError,
        error,
        clear,
        isItemLoaded,
        loadMore,
        reload
    };
}

export default useHubwiseJobs;